import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL, configService } from '../services/api';
import { cacheClearAll } from '../services/offlineCache';
import { setLanguage } from '../i18n';
import { FEATURES } from '../config/features';
import { flushOutbox } from '../services/offlineSync';

const AuthContext = createContext(null);

const API = `${BACKEND_URL}/api`;

const RETRY_DELAY_MS = 5000;

const _setBrikopsCookie = () => {
  document.cookie = 'brikops_logged_in=1; domain=.brikops.com; path=/; max-age=2592000; SameSite=Lax; Secure';
};
const _clearBrikopsCookie = () => {
  document.cookie = 'brikops_logged_in=; domain=.brikops.com; path=/; max-age=0';
};

const _isAuthError = (error) => {
  const status = error?.response?.status;
  if (status === 403 && error?.response?.data?.detail?.code === 'pending_deletion') return false;
  return status === 401 || status === 403;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [features, setFeatures] = useState(null);
  const [networkError, setNetworkError] = useState(false);
  const [token, setToken] = useState(() => {
    const hash = window.location.hash;
    if (hash) {
      const hashParams = new URLSearchParams(hash.substring(1));
      const hashToken = hashParams.get('token');
      if (hashToken) {
        localStorage.setItem('token', hashToken);
        _setBrikopsCookie();
        window.history.replaceState({}, '', window.location.pathname + window.location.search);
        return hashToken;
      }
    }
    return localStorage.getItem('token');
  });
  const [loading, setLoading] = useState(true);
  const retryTimerRef = useRef(null);
  const toastShownRef = useRef(false);
  const sessionExpiredRef = useRef(false);

  useEffect(() => {
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(undefined, (error) => {
      if (
        error.response?.status === 403 &&
        error.response?.data?.detail?.code === 'pending_deletion'
      ) {
        error._pendingDeletion = true;
        setUser(prev => prev ? { ...prev, user_status: 'pending_deletion' } : prev);
        toast.dismiss();
      }
      return Promise.reject(error);
    });
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  // Mid-session 401: clear session + redirect to /login. Lives here (not in
  // api.js) per the #171 guidance. AuthProvider is outside BrowserRouter so
  // we use a full page reload instead of useNavigate.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(undefined, (error) => {
      if (error?.response?.status !== 401) return Promise.reject(error);
      const url = error.config?.url || '';
      if (url.includes('/auth/')) return Promise.reject(error);
      if (!localStorage.getItem('token')) return Promise.reject(error);
      if (sessionExpiredRef.current) return Promise.reject(error);
      sessionExpiredRef.current = true;
      setToken(null);
      setUser(null);
      setNetworkError(false);
      localStorage.removeItem('token');
      try { localStorage.removeItem('brikops_offline_user'); } catch (_) {}
      _clearBrikopsCookie();
      toast.info('החיבור פג, אנא התחבר מחדש');
      setTimeout(() => { window.location.href = '/login'; }, 1500);
      return Promise.reject(error);
    });
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  const fetchCurrentUser = useCallback(async (isRetry = false) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userData = response.data;
      if (userData.role === 'contractor' && userData.preferred_language) {
        setLanguage(userData.preferred_language);
      }
      setUser(userData);
      // Offline cache hygiene: if a DIFFERENT user authenticated than the one
      // whose data is cached on this device, wipe the cache before new field
      // GETs repopulate it (shared-device safety). Fail-soft — never blocks
      // bootstrap. Gated implicitly: cacheClearAll is a no-op when the store
      // is empty / IndexedDB unavailable.
      try {
        const uid = String(userData?.id ?? userData?.user_id ?? userData?.phone ?? userData?.email ?? '');
        const lastUid = localStorage.getItem('brikops_last_uid') || '';
        if (uid && uid !== lastUid) {
          cacheClearAll();
          localStorage.setItem('brikops_last_uid', uid);
          // Different user — drop the previous user's offline snapshot before
          // the new one is written below (never surface a prior identity).
          try { localStorage.removeItem('brikops_offline_user'); } catch (_) {}
        }
      } catch (_) { /* cache hygiene must never break login */ }
      // BATCH 3a-bis (A.1): store the user PROFILE (no new credential — the JWT
      // already lives in localStorage) so a cold launch while offline can enter
      // with the cached identity. Same hygiene as the cache (cleared on logout /
      // auth-error / user-change). Never logged.
      if (FEATURES.OFFLINE_MODE) {
        try { localStorage.setItem('brikops_offline_user', JSON.stringify(userData)); }
        catch (_) { /* never break login */ }
        // (B.2) bootstrap success ⇒ network is genuinely usable ⇒ drain backlog.
        try { flushOutbox().catch(() => {}); } catch (_) {}
      }
      setNetworkError(false);
      toastShownRef.current = false;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    } catch (error) {
      if (_isAuthError(error)) {
        setToken(null);
        setUser(null);
        setNetworkError(false);
        localStorage.removeItem('token');
        try { localStorage.removeItem('brikops_offline_user'); } catch (_) {}
        _clearBrikopsCookie();
        if (retryTimerRef.current) {
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = null;
        }
      } else {
        // BATCH 3a-bis (A.2): NETWORK bootstrap failure (this branch is never an
        // auth error). If we hold a saved snapshot + token, ENTER offline with
        // the cached identity instead of the blocking retry screen. The retry
        // loop below is UNCHANGED, so the server revalidates the moment network
        // returns — fresh user replaces the snapshot, or a 401/403 logs out as
        // today. The snapshot NEVER overrides a real auth rejection.
        let enteredOffline = false;
        if (FEATURES.OFFLINE_MODE && !_isAuthError(error)) {
          try {
            const raw = localStorage.getItem('brikops_offline_user');
            if (raw && localStorage.getItem('token')) {
              const snap = JSON.parse(raw);
              if (snap && typeof snap === 'object' && snap.id) {
                setUser(snap);
                enteredOffline = true;
              }
            }
          } catch (_) { /* fall through to the retry screen */ }
        }
        setNetworkError(!enteredOffline);
        if (isRetry && !toastShownRef.current) {
          toast.error('בעיית חיבור');
          toastShownRef.current = true;
        }
        const savedToken = token;
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          if (savedToken === localStorage.getItem('token')) {
            fetchCurrentUser(true);
          }
        }, RETRY_DELAY_MS);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchFeatures = useCallback(async () => {
    try {
      const data = await configService.getFeatures();
      setFeatures(data?.feature_flags || {});
    } catch {
      setFeatures({});
    }
  }, []);

  useEffect(() => {
    if (token) {
      toastShownRef.current = false;
      fetchCurrentUser(false);
      fetchFeatures();
    } else {
      setLoading(false);
    }
  }, [token, fetchCurrentUser, fetchFeatures]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const data = response.data;
      if (!data || !data.token) {
        console.error('[AUTH] Login response missing token', { status: response.status, hasData: !!data });
        return { success: false, error: 'תגובה לא תקינה מהשרת' };
      }
      const { token: newToken, user: userData, platform_role } = data;
      if (userData?.role === 'contractor' && userData?.preferred_language) {
        setLanguage(userData.preferred_language);
      }
      setToken(newToken);
      setUser({ ...userData, platform_role: platform_role || 'none' });
      try { localStorage.setItem('token', newToken); } catch (e) { console.warn('[AUTH] localStorage write failed', e); }
      try { _setBrikopsCookie(); } catch (e) { /* ignore cookie errors */ }
      return { success: true };
    } catch (error) {
      console.error('[AUTH] Login error', error?.message, error?.response?.status, error?.stack);
      const detail = error.response?.data?.detail;
      return {
        success: false,
        error: detail || (error.message ? `שגיאה טכנית: ${error.message}` : 'Login failed')
      };
    }
  };

  const loginWithOtp = (newToken, userData, platformRole) => {
    if (userData?.role === 'contractor' && userData?.preferred_language) {
      setLanguage(userData.preferred_language);
    }
    setToken(newToken);
    setUser({ ...userData, platform_role: platformRole || userData?.platform_role || 'none' });
    try { localStorage.setItem('token', newToken); } catch (e) { console.warn('[AUTH] localStorage write failed', e); }
    try { _setBrikopsCookie(); } catch (e) { /* ignore */ }
  };

  const register = async (userData) => {
    try {
      await axios.post(`${API}/auth/register`, userData);
      return await login(userData.email, userData.password);
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const logout = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    setToken(null);
    setUser(null);
    setNetworkError(false);
    localStorage.removeItem('token');
    try { localStorage.removeItem('brikops_offline_user'); } catch (_) {}
    _clearBrikopsCookie();
    // Wipe the offline read-cache so the next user on a shared device can't
    // be served the previous user's cached project data. Fire-and-forget,
    // fail-soft — cacheClearAll never throws or blocks.
    try { cacheClearAll(); } catch (_) { /* never block logout */ }
  };

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch {}
  }, [token]);

  const forceUserStatus = useCallback((status) => {
    setUser(prev => prev ? { ...prev, user_status: status } : prev);
  }, []);

  const replaceToken = useCallback((newToken) => {
    if (!newToken) return;
    setToken(newToken);
    try { localStorage.setItem('token', newToken); } catch (e) { console.warn('[AUTH] localStorage write failed', e); }
  }, []);

  const value = {
    user,
    token,
    loading,
    features,
    networkError,
    login,
    loginWithOtp,
    register,
    logout,
    refreshUser,
    forceUserStatus,
    replaceToken,
    isAuthenticated: !!token && !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
